import { useMemo } from 'react';

import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { centsToDollarString } from '@fhs-legacy/frontend/src/utils/index';
import { isDelivery } from '@fhs-legacy/frontend/src/utils/service-mode';

import { usePaymentMethodStore } from '../payment';
import { useTipStore } from '../state/tip-store';

import { useSelectedGiftCard } from './use-selected-gift-card';

export function useGiftCardCheckoutValidation() {
  const { serverOrder } = useCartContext();
  const { serviceMode } = useServiceModeContext();
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const selectedGiftCard = useSelectedGiftCard();
  const tipCents = useTipStore(state => state.totalTipCentsForCommit);
  const totalCents = (serverOrder?.cart?.totalCents || 0) + (isDelivery(serviceMode) ? tipCents : 0);

  // Extract primitive values for stable memoization
  const currentBalance = selectedGiftCard?.currentBalance;
  const isBalanceLoading = selectedGiftCard?.isBalanceLoading ?? false;
  const hasGiftCard = Boolean(selectedGiftCard);
  // Same gate as delivery-fees.tsx skeleton
  const delivery = serverOrder?.delivery;

  return useMemo(() => {
    const errorGiftCardBalanceNotEnough =
      selectedPaymentMethod === 'giftCard' &&
      hasGiftCard &&
      (!isDelivery(serviceMode) || delivery) &&
      (currentBalance === undefined || currentBalance < totalCents);

    const giftCardDueAmountInCents = totalCents - (currentBalance ?? 0);

    const giftCardMissingAmount =
      errorGiftCardBalanceNotEnough && currentBalance !== undefined
        ? centsToDollarString(giftCardDueAmountInCents)
        : undefined;

    const showReloadGiftCardCTA = hasGiftCard && errorGiftCardBalanceNotEnough;

    return {
      errorGiftCardBalanceNotEnough,
      giftCardMissingAmount,
      showReloadGiftCardCTA,
      selectedGiftCard,
      giftCardDueAmountInCents,
      isBalanceLoading,
    };
  }, [
    hasGiftCard,
    currentBalance,
    totalCents,
    selectedPaymentMethod,
    selectedGiftCard,
    isBalanceLoading,
    serviceMode,
    delivery,
  ]);
}
